import { practiceUpgradeModalOpenAtom } from '@/atoms/paywallAtoms';
import { Button } from '@heidi/design-system/button';
import { TierPracticeFilled, TierProFilled } from '@heidi/design-system/icons';
import { useOpenEvidenceUpgradeModal } from '@/features/growth/deprecate-evidence-plus/useOpenEvidenceUpgradeModal';
import { useQueryUser } from '@heidi/workspace/data';
import { useQueryUserBillingInfo } from '@/hooks/query/useQueryUserBillingInfo';
import { useEvidenceBillingInfo } from '@/hooks/useEvidenceBillingInfo';
import useReverseTrial from '@/hooks/useReverseTrial';
import { SignupSource, SubscriptionTier } from '@heidi/schema/ml-scribe';
import { type TranslationKey, useTranslations } from '@heidi/runtime/i18n';
import { useSetAtom } from 'jotai';
import { useCallback } from 'react';

export const EvidenceUpgradeCtaButton = () => {
  const { trialDaysRemaining: evidenceTrialDaysRemaining, hasTrialExpired: evidenceTrialExpired } = useEvidenceBillingInfo();
  const {
    clinicianTrialEnded,
    clinicianTrialDaysLeft: clinicianDaysLeft,
    isClinicianPracticeTrialUser,
    isEligibleForPracticeTrial,
  } = useReverseTrial();
  const { data: signupSource } = useQueryUser((user) => user?.signup_source);
  const { data: newUserTier } = useQueryUserBillingInfo((user) => user?.new_product_tier);
  const { t } = useTranslations();
  const { openEvidenceUpgradeModal } = useOpenEvidenceUpgradeModal();
  const setPracticeUpgradeModalOpen = useSetAtom(practiceUpgradeModalOpenAtom);
  const isPracticeEligible = isClinicianPracticeTrialUser || isEligibleForPracticeTrial;
  const isScribePlusUser = newUserTier === SubscriptionTier.PRO;
  const trialEnded = signupSource === SignupSource.evidence ? evidenceTrialExpired : clinicianTrialEnded;
  const trialDays = signupSource === SignupSource.evidence ? evidenceTrialDaysRemaining : clinicianDaysLeft;

  const handleClick = useCallback(() => {
    if (signupSource === SignupSource.practice || isPracticeEligible) {
      setPracticeUpgradeModalOpen(true);
      return;
    }

    openEvidenceUpgradeModal({
      isScribePlusUser,
      isClinicianTrialUser: signupSource !== SignupSource.evidence,
      isPracticeUpgradeTarget: false,
    });
  }, [
    openEvidenceUpgradeModal,
    setPracticeUpgradeModalOpen,
    signupSource,
    isPracticeEligible,
    isScribePlusUser,
  ]);

  return (
    <Button
      variant={isPracticeEligible ? 'pro_plus' : 'pro'}
      size="md"
      onClick={handleClick}
      aria-label={
        trialEnded
          ? t('evidenceProTrial.headerButton.upgrade' as TranslationKey)
          : t('evidenceProTrial.headerButton.daysLeft' as TranslationKey, {
              days: trialDays,
            })
      }
      data-testid="chat-header-trial-days-button"
    >
      {isPracticeEligible ? (
        <TierPracticeFilled className="shrink-0" />
      ) : (
        <TierProFilled className="shrink-0" />
      )}
      {trialEnded
        ? t('evidenceProTrial.headerButton.upgrade' as TranslationKey)
        : t('evidenceProTrial.headerButton.daysLeft' as TranslationKey, {
            days: trialDays,
          })}
    </Button>
  );
};
